import React from 'react';
import {
  ArrowRight,
  ArrowUpRight,
  Sun,
  CheckCircle2,
  HelpCircle,
  BarChart3,
  Layers,
  MapPin,
  FileSpreadsheet,
  Sliders,
} from 'lucide-react';
import { Navbar } from '../../components/common/Navbar';
import { Footer } from '../../components/common/Footer';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { SectionHeader } from '../../components/common/SectionHeader';
import { ONE_MINUTE_SOLAR_FAQS } from '../../data/siteData';
import { SEO } from '../../components/common/SEO';

export default function OneMinuteSolarPage() {
  return (
    <div className="min-h-screen flex flex-col bg-white text-navy">
      <SEO
        title="1minute solar｜自家消費型太陽光の導入判断支援"
        description="住所と電力情報から自家消費型太陽光の導入効果を素早くシミュレーション。Spaq株式会社の1minute solarをご紹介します。"
        canonicalPath="/products/1minute-solar"
      />
      <Navbar />

      <main className="flex-1">
        {/* Hero */}
        <section className="bg-hero-gradient border-b border-surface-border py-14 md:py-20">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7 space-y-5">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="gold">導入前の判断を支えるソフトウェア</Badge>
                  <Badge variant="slate">自家消費型太陽光シミュレーター</Badge>
                </div>

                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-navy tracking-tight leading-tight">
                  太陽光を導入する前に、
                  <br />
                  <span className="text-spaq-cyan-dark">相性と経済性</span>を確かめる。
                </h1>

                <p className="text-base sm:text-lg text-navy-muted leading-relaxed">
                  <strong className="text-navy">「1minute solar」</strong>
                  は、施設のエリア・屋根条件・普段の電気の使い方から、自家消費型太陽光発電の導入効果と適正規模を素早く試算・比較できるソフトウェアです。「たくさん載せすぎて電気が余る」といった失敗を防ぎ、納得感のある初期判断を支えます。
                </p>

                <div className="pt-2 flex flex-col sm:flex-row gap-3.5">
                  <Button
                    href="https://1minute.spaq.ltd/"
                    variant="navy"
                    size="lg"
                    icon={<ArrowUpRight className="w-4 h-4" />}
                  >
                    1minute solar シミュレーターを開く
                  </Button>
                  <Button
                    to="/contact?type=1minute-solar"
                    variant="primary"
                    size="lg"
                    icon={<ArrowRight className="w-4 h-4" />}
                  >
                    正式設計・個別試算を相談する
                  </Button>
                </div>
              </div>

              {/* Screen Image Preview Card */}
              <div className="lg:col-span-5">
                <div className="bg-white rounded-3xl border border-surface-border shadow-soft-lg p-6">
                  <div className="flex items-center justify-between mb-4 pb-3 border-b border-surface-border">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-rose-400" />
                      <div className="w-3 h-3 rounded-full bg-amber-400" />
                      <div className="w-3 h-3 rounded-full bg-emerald-400" />
                      <span className="text-xs font-mono text-navy-muted ml-2">
                        1minute solar 診断プレビュー
                      </span>
                    </div>
                    <Badge variant="slate" size="sm">画面イメージ</Badge>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-2.5">
                      <div className="p-3 rounded-xl bg-surface-warm border border-spaq-gold/30">
                        <div className="text-[10px] text-navy-muted">推奨パネル容量</div>
                        <div className="text-lg font-bold text-navy font-mono-num">180 kW</div>
                        <div className="text-[10px] text-spaq-gold-dark font-semibold">需要バランス最適</div>
                      </div>
                      <div className="p-3 rounded-xl bg-surface-sky border border-spaq-cyan/30">
                        <div className="text-[10px] text-navy-muted">想定自家消費率</div>
                        <div className="text-lg font-bold text-spaq-cyan-dark font-mono-num">93.5 %</div>
                        <div className="text-[10px] text-spaq-cyan-dark font-semibold">余剰ロス最小化</div>
                      </div>
                      <div className="p-3 rounded-xl bg-surface-slate border border-surface-border">
                        <div className="text-[10px] text-navy-muted">投資回収目安</div>
                        <div className="text-lg font-bold text-navy font-mono-num">約 6.8 年</div>
                        <div className="text-[10px] text-navy-muted">※試算条件による</div>
                      </div>
                    </div>

                    {/* Visual Chart Comparison */}
                    <div className="p-4 rounded-2xl bg-surface-slate border border-surface-border space-y-3">
                      <div className="text-xs font-bold text-navy">
                        容量パターン別の「使い切れる電気」比較（試算例）
                      </div>
                      <div className="space-y-2 text-xs">
                        <div>
                          <div className="flex justify-between mb-1">
                            <span className="font-medium text-navy">A案：小規模（100kW）</span>
                            <span className="text-navy-muted">自家消費 98% ／ 削減額 やや少</span>
                          </div>
                          <div className="h-2.5 bg-white rounded-full overflow-hidden flex border border-surface-border">
                            <div className="w-[55%] bg-spaq-cyan" />
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between mb-1">
                            <span className="font-bold text-spaq-cyan-dark">★ B案：推奨最適（180kW）</span>
                            <span className="font-bold text-spaq-cyan-dark">自家消費 93.5% ／ 投資効率 最適</span>
                          </div>
                          <div className="h-2.5 bg-white rounded-full overflow-hidden flex border border-spaq-cyan">
                            <div className="w-[82%] bg-spaq-cyan" />
                            <div className="w-[6%] bg-amber-300" />
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between mb-1">
                            <span className="font-medium text-navy-muted">C案：屋根最大（300kW）</span>
                            <span className="text-rose-600">休日の余剰ロス増（自家消費 71%）</span>
                          </div>
                          <div className="h-2.5 bg-white rounded-full overflow-hidden flex border border-surface-border">
                            <div className="w-[70%] bg-slate-400" />
                            <div className="w-[30%] bg-rose-300" />
                          </div>
                        </div>
                      </div>
                      <div className="text-[10px] text-navy-muted pt-1">
                        ※数値・グラフは説明用の画面イメージ（試算条件に基づく試算例）です。
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Step 1 & 2: 利用目的と入力条件 */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader
              tag="WHY 1MINUTE SOLAR"
              title="なぜ、導入前の「1minute solar」が必要なのか"
              subtitle="自家消費型太陽光では「屋根にどれだけ載るか」ではなく「施設が昼間にどれだけ電気を使っているか」が採算のカギを握ります。"
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
              {[
                {
                  icon: <MapPin className="w-6 h-6 text-spaq-gold-dark" />,
                  title: '1. 簡単な初期条件からスタート',
                  desc: '施設のエリア（日射条件）、おおよその屋根面積、月々の電気代や業種・稼働カレンダーなど、手元にある情報から気軽に試算を始められます。',
                },
                {
                  icon: <Layers className="w-6 h-6 text-spaq-cyan-dark" />,
                  title: '2. 複数パターンを並べて比較',
                  desc: '「屋根いっぱいに載せた場合」と「需要に合わせて絞った場合」など、複数の容量パターンで自家消費率と投資回収年数を比較できます。',
                },
                {
                  icon: <BarChart3 className="w-6 h-6 text-navy" />,
                  title: '3. 社内稟議・意思決定のたたき台に',
                  desc: '「 вообще太陽光を検討する価値がある施設なのか」を早期に判断でき、見送りも含めた健全な経営判断に役立ちます。',
                },
              ].map((card, i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-surface-border bg-surface-slate/60 p-7 shadow-soft"
                >
                  <div className="w-12 h-12 rounded-2xl bg-white border border-surface-border flex items-center justify-center mb-4">
                    {card.icon}
                  </div>
                  <h3 className="text-lg font-bold text-navy mb-2">{card.title}</h3>
                  <p className="text-sm text-navy-muted leading-relaxed">{card.desc}</p>
                </div>
              ))}
            </div>

            {/* 入力条件と出力項目 */}
            <div className="rounded-3xl border border-surface-border bg-surface-sky p-7 sm:p-10">
              <h3 className="text-xl font-bold text-navy mb-6">
                入力できる主な条件と、比較・確認できる結果
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-white rounded-2xl p-6 border border-surface-border">
                  <div className="text-xs font-bold text-spaq-gold-dark uppercase tracking-wider mb-3">
                    INPUT｜入力条件の例
                  </div>
                  <ul className="space-y-2.5 text-sm text-navy">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-spaq-gold-dark shrink-0 mt-0.5" />
                      <span>設置エリア（都道府県・地域の日射量データと自動連携）</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-spaq-gold-dark shrink-0 mt-0.5" />
                      <span>屋根の広さ・向き、または検討したいパネル容量</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-spaq-gold-dark shrink-0 mt-0.5" />
                      <span>施設の業種・稼働日パターン（平日稼働／年中無休など）</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-spaq-gold-dark shrink-0 mt-0.5" />
                      <span>現在の契約電力・月間の電気使用量または電気料金目安</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-2xl p-6 border border-surface-border">
                  <div className="text-xs font-bold text-spaq-cyan-dark uppercase tracking-wider mb-3">
                    OUTPUT｜比較・確認できること
                  </div>
                  <ul className="space-y-2.5 text-sm text-navy">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-spaq-cyan-dark shrink-0 mt-0.5" />
                      <span>年間想定発電量と、施設内で使い切れる「自家消費電力量」</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-spaq-cyan-dark shrink-0 mt-0.5" />
                      <span>休日や中間期に使い切れず余る「余剰ロス」の割合</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-spaq-cyan-dark shrink-0 mt-0.5" />
                      <span>年間の電気代削減目安・CO2削減効果の概算</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-spaq-cyan-dark shrink-0 mt-0.5" />
                      <span>初期投資目安に対する投資回収年数の比較</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 正式設計までの流れ */}
        <section className="py-16 md:py-24 bg-surface-slate border-y border-surface-border">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader
              tag="WORKFLOW"
              title="概算シミュレーションから、正式設計・導入までの流れ"
              subtitle="1minute solarで「可能性がありそうだ」と分かったら、30分デマンドデータや現地確認を組み合わせた精密設計へとスムーズに進めます。"
            />

            <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
              {[
                {
                  step: 'STEP 01',
                  title: '1minute solarで概算把握',
                  desc: 'まずはWebシミュレーターまたは初回ヒアリングで、施設と太陽光の相性・おおよその規模感を確認します。',
                },
                {
                  step: 'STEP 02',
                  title: '30分デマンドデータ分析',
                  desc: '電力会社の30分電力量データ（1年分）をお預かりし、時間ごとの需要波形と発電波形の重なりを精密に検証します。',
                },
                {
                  step: 'STEP 03',
                  title: '現地調査・最適レイアウト設計',
                  desc: '屋根の強度・日影・受変電設備を確認し、必要に応じて蓄電池や「SPAQ CORE」も含めた正式設計・お見積りを作成します。',
                },
                {
                  step: 'STEP 04',
                  title: '施工・連系・運用スタート',
                  desc: '補助金申請や電力会社との連系協議、安全施工を経て運用を開始。導入後の電力制御・保守まで一貫して支えます。',
                },
              ].map((s, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-2xl border border-surface-border p-6 shadow-soft"
                >
                  <div className="text-xs font-mono font-bold text-spaq-gold-dark mb-2">
                    {s.step}
                  </div>
                  <h3 className="text-base font-bold text-navy mb-2">{s.title}</h3>
                  <p className="text-xs text-navy-muted leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24 bg-white">
          <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
            <SectionHeader
              tag="FAQ"
              title="1minute solar に関するよくあるご質問"
              align="center"
            />

            <div className="space-y-4">
              {ONE_MINUTE_SOLAR_FAQS.map((faq, idx) => (
                <div
                  key={idx}
                  className="rounded-2xl border border-surface-border bg-surface-slate/50 p-6"
                >
                  <div className="flex items-start gap-3 mb-2">
                    <HelpCircle className="w-5 h-5 text-spaq-cyan-dark shrink-0 mt-0.5" />
                    <h3 className="text-base font-bold text-navy">{faq.q}</h3>
                  </div>
                  <p className="text-sm text-navy-muted leading-relaxed pl-8">{faq.a}</p>
                </div>
              ))}
            </div>

            {/* Bottom CTA Box */}
            <div className="mt-14 rounded-3xl bg-navy text-white p-8 sm:p-10 text-center space-y-5">
              <h2 className="text-2xl sm:text-3xl font-bold">
                まずはあなたの施設で、太陽光の相性を確かめてみませんか？
              </h2>
              <p className="text-sm sm:text-base text-slate-300 max-w-2xl mx-auto">
                Web上で今すぐ試せる「1minute solar」のご利用はもちろん、当社エンジニアによる30分デマンドデータを用いた個別診断・販売パートナー様からのご相談も承っております。
              </p>
              <div className="pt-2 flex flex-col sm:flex-row justify-center gap-4">
                <Button
                  href="https://1minute.spaq.ltd/"
                  variant="primary"
                  size="lg"
                  icon={<ArrowUpRight className="w-4 h-4" />}
                >
                  1minute solar シミュレーターを開く
                </Button>
                <Button
                  to="/contact?type=1minute-solar"
                  variant="outline-light"
                  size="lg"
                  icon={<ArrowRight className="w-4 h-4" />}
                >
                  正式検討・個別試算を相談する
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
